import React, { useState } from 'react';
import { X, User, Calendar, Utensils, Receipt, Phone, Mail, Badge, Bot, LogIn, LogOut, FileText, Send, MoreHorizontal } from 'lucide-react';
import { Room, Order, Booking } from '../types';
import { format } from 'date-fns';
import { cn } from '../utils/cn';
import InvoiceGenerationModal from './InvoiceGenerationModal';

interface RoomDetailModalProps {
  room?: Room;
  orders: Order[];
  bookings: Booking[];
  activeBooking?: Booking;
  isLoading: boolean;
  onClose: () => void;
}

const RoomDetailModal: React.FC<RoomDetailModalProps> = ({
  room,
  orders,
  bookings,
  activeBooking,
  isLoading,
  onClose,
}) => {
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const calculateTotalAmount = () => {
    return orders.reduce((total, order) => total + parseFloat(order.totalAmount), 0);
  };

  const calculateStayDuration = () => {
    if (!activeBooking) return 0;
    const checkin = new Date(activeBooking.originalCheckinDate);
    const checkout = new Date(activeBooking.currentCheckoutDate);
    return Math.ceil((checkout.getTime() - checkin.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-2xl p-8 max-w-md mx-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-600">Loading room details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          margin: 0, 
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          zIndex: 9999
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden" 
          style={{ 
            margin: 0,
            backgroundColor: 'white',
            borderRadius: '1rem'
          }}
        >
        {/* Header - Already styled with primary color */}
        <div className="bg-primary-500 text-white p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">R</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Room {room?.roomId || 'Unknown'}</h2>
              {room?.guestName && (
                <p className="text-primary-100">Guest: {room.guestName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* AI Overview Section */}
          {activeBooking && (
            <div className="mb-6 glass-card p-4 rounded-lg border-l-4 border-purple-500">
              <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                <Bot className="w-5 h-5 text-purple-600" />
                <span>AI Guest Profile Overview</span>
              </h3>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Guest Behavior Analysis</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Prefers early morning check-ins (avg 8:30 AM)</li>
                      <li>• Frequent room service orders (3-4 times per stay)</li>
                      <li>• Values privacy and quiet environment</li>
                      <li>• Loyal customer with 5+ previous stays</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Service Recommendations</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Suggest spa services (previous interest shown)</li>
                      <li>• Offer late checkout (frequent request)</li>
                      <li>• Recommend premium dining options</li>
                      <li>• Consider room upgrade for loyalty</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-yellow-50 rounded border-l-2 border-yellow-400">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Guest has extended stay 2 times previously. Consider offering flexible checkout options.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Stay Information */}
              <div className="glass-card p-4 rounded-lg">
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span>Stay Information</span>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-blue-600 font-medium">Check-in</div>
                    <div className="font-bold">
                      {room?.stayStartDate ? format(new Date(room.stayStartDate), 'MMM dd, yyyy') : 'Not set'}
                    </div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-sm text-orange-600 font-medium">Check-out</div>
                    <div className="font-bold">
                      {room?.stayEndDate ? format(new Date(room.stayEndDate), 'MMM dd, yyyy') : 'Not set'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Guest Information */}
              {activeBooking && (
                <div className="glass-card p-4 rounded-lg">
                  <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                    <User className="w-5 h-5 text-purple-600" />
                    <span>Guest Information</span>
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <User className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">{activeBooking.guestName}</span>
                    </div>
                    {activeBooking.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-4 h-4 text-purple-600" />
                        <span>{activeBooking.phone}</span>
                      </div>
                    )}
                    {activeBooking.email && (
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-purple-600" />
                        <span>{activeBooking.email}</span>
                      </div>
                    )}
                    {activeBooking.idProof && (
                      <div className="flex items-center space-x-3">
                        <Badge className="w-4 h-4 text-purple-600" />
                        <span>{activeBooking.idProof}</span>
                      </div>
                    )}
                    {activeBooking.extensionCount > 0 && (
                      <div className="text-sm text-orange-600">
                        Extensions: {activeBooking.extensionCount} time(s)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Booking Information */}
              <div className="glass-card p-4 rounded-lg">
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span>Booking Information</span>
                </h3>
                {activeBooking && (
                  <div className="bg-green-50 p-3 rounded-lg mb-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-bold text-green-700">Active Booking</span>
                    </div>
                    <div className="text-sm space-y-1">
                      <div>Guest: {activeBooking.guestName}</div>
                      <div>Check-in: {format(new Date(activeBooking.originalCheckinDate), 'MMM dd, yyyy')}</div>
                      <div>Check-out: {format(new Date(activeBooking.currentCheckoutDate), 'MMM dd, yyyy')}</div>
                      {activeBooking.extensionCount > 0 && (
                        <div className="text-orange-600">Extensions: {activeBooking.extensionCount}</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  {bookings.length} booking(s) found
                </div>
              </div>

              {/* Pricing & Charges */}
              {activeBooking && (
                <div className="glass-card p-4 rounded-lg">
                  <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                    <Receipt className="w-5 h-5 text-purple-600" />
                    <span>Pricing & Charges</span>
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Room Charges */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Room Charges</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Room Type: {activeBooking.roomType}</span>
                          <span className="font-medium">₹{room?.roomType === 'Deluxe' ? '2500' : room?.roomType === 'Suite' ? '3500' : '1500'}/night</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Duration: {calculateStayDuration()} night(s)</span>
                          <span className="font-medium">× {calculateStayDuration()}</span>
                        </div>
                        <div className="border-t border-blue-200 pt-2">
                          <div className="flex justify-between font-semibold">
                            <span>Subtotal:</span>
                            <span>₹{((room?.roomType === 'Deluxe' ? 2500 : room?.roomType === 'Suite' ? 3500 : 1500) * calculateStayDuration()).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* F&B Charges */}
                    <div className="bg-green-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">Food & Beverage</h4>
                      <div className="space-y-2">
                        {orders.length === 0 ? (
                          <div className="text-sm text-gray-500">No F&B orders</div>
                        ) : (
                          <>
                            {orders.map((order) => (
                              <div key={order.orderId} className="flex justify-between text-sm">
                                <span>{order.itemName} × {order.quantity}</span>
                                <span>₹{order.totalAmount}</span>
                              </div>
                            ))}
                            <div className="border-t border-green-200 pt-2">
                              <div className="flex justify-between font-semibold">
                                <span>F&B Subtotal:</span>
                                <span>₹{calculateTotalAmount().toLocaleString()}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Misc Services */}
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-orange-800 mb-2">Miscellaneous Services</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Laundry Service</span>
                          <span>₹0</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Room Service</span>
                          <span>₹0</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Spa & Wellness</span>
                          <span>₹0</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Airport Transfer</span>
                          <span>₹0</span>
                        </div>
                        <div className="border-t border-orange-200 pt-2">
                          <div className="flex justify-between font-semibold">
                            <span>Misc Subtotal:</span>
                            <span>₹0</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Taxes & Fees */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">Taxes & Fees</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>GST (18%)</span>
                          <span>₹{Math.round((activeBooking.totalAmount * 0.18)).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Service Charge (10%)</span>
                          <span>₹{Math.round((activeBooking.totalAmount * 0.10)).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>City Tax</span>
                          <span>₹{Math.round((activeBooking.totalAmount * 0.05)).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="bg-primary-50 p-4 rounded-lg border-2 border-primary-200">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-primary-800">TOTAL AMOUNT:</span>
                        <span className="text-2xl font-bold text-primary-600">₹{activeBooking.totalAmount.toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-primary-700 mt-1">
                        Payment Status: {activeBooking.bookingStatus === 'confirmed' ? 'Pending' : 'Paid'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Food Orders */}
              <div className="glass-card p-4 rounded-lg">
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                  <Utensils className="w-5 h-5 text-green-600" />
                  <span>Food Orders</span>
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
                    {orders.length} orders
                  </span>
                </h3>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No orders found
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map((order) => (
                      <div key={order.orderId} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{order.itemName}</span>
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            order.status === 'completed' 
                              ? "bg-green-100 text-green-700" 
                              : "bg-orange-100 text-orange-700"
                          )}>
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Qty: {order.quantity} • {format(new Date(order.orderTime), 'MMM dd, HH:mm')}</span>
                          <span className="font-bold text-green-600">₹{order.totalAmount}</span>
                        </div>
                        {order.quantity > 1 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Unit Price: ₹{order.unitPrice}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="flex justify-between font-bold">
                        <span>TOTAL:</span>
                        <span className="text-primary-600">₹{calculateTotalAmount().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Misc Services */}
              <div className="glass-card p-4 rounded-lg">
                <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
                  <Utensils className="w-5 h-5 text-indigo-600" />
                  <span>Misc Services</span>
                </h3>
                <div className="space-y-2">
                  {[
                    'Laundry Service',
                    'Room Service',
                    'Spa & Wellness',
                    'Airport Transfer',
                    'Concierge Service',
                    'Late Checkout',
                    'Extra Bed',
                    'Mini Bar',
                  ].map((service) => (
                    <div key={service} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="font-medium">{service}</span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                        NOT AVAILED
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Invoice Button for Checked-out Guests */}
              {activeBooking && activeBooking.bookingStatus === 'checked_out' && (
                <button className="w-full btn-primary flex items-center justify-center space-x-2 py-3">
                  <Receipt className="w-5 h-5" />
                  <span>Show Invoice</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {activeBooking ? `Booking ID: ${activeBooking.bookingId}` : 'No active booking'}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              {/* Check-in Button */}
              {activeBooking && activeBooking.bookingStatus === 'confirmed' && (
                <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  <LogIn className="w-4 h-4" />
                  <span>Check In</span>
                </button>
              )}
              
              {/* Check-out Button */}
              {activeBooking && activeBooking.bookingStatus === 'checked_in' && (
                <button className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                  <LogOut className="w-4 h-4" />
                  <span>Check Out</span>
                </button>
              )}
              
              {/* Generate Invoice Button */}
              {activeBooking && (
                <button 
                  onClick={() => setShowInvoiceModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span>Generate Invoice</span>
                </button>
              )}
              
              {/* More Actions Button */}
              <button className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                <MoreHorizontal className="w-4 h-4" />
                <span>More</span>
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Invoice Generation Modal */}
      {showInvoiceModal && (
        <InvoiceGenerationModal
          orders={orders}
          activeBooking={activeBooking}
          onClose={() => setShowInvoiceModal(false)}
        />
      )}
    </>
  );
};

export default RoomDetailModal;

